/**
 * IndustryNewsCard — full-width SEO industry news reader on the dashboard.
 *
 * Design: each article is its own tinted card (not a table row), with a
 * subtle background wash + a 3px accent border on the left matching the
 * source's assigned colour. Same-source cards naturally cluster in the
 * grid because the server returns them source-contiguous, and the shared
 * colour makes the grouping readable at a glance even when the grid
 * reflows to a different column count.
 *
 * Palette draws from colours we already have dark-mode overrides for in
 * index.css, so there's no new CSS surface to maintain.
 */
import React from "react";
import { Newspaper, ArrowUpRight, Loader2, RefreshCw } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso).getTime();
  if (Number.isNaN(d)) return "";
  const mins = Math.max(1, Math.round((Date.now() - d) / 60000));
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

/* --- Source colour map --------------------------------------------------
 * Each entry references Tailwind-arbitrary utility classes that already
 * have dark-mode overrides in index.css. If an admin adds a new source
 * that isn't in this map we fall back to a hash-based pick from the same
 * palette so every card still gets a tint.
 */
const PALETTE = [
  // bg:       card tint
  // accent:   left-border strip (sharper than bg)
  // pill:     source badge background
  // pillText: source badge text
  { key: "cyan",   bg: "bg-[#ecfeff]",   accent: "border-l-[#67e8f9]",   pill: "bg-[#ecfeff]",   pillText: "text-[#0e7490]" },
  { key: "amber",  bg: "bg-[#fffaf0]",   accent: "border-l-[#feb272]",   pill: "bg-[#fffaf0]",   pillText: "text-[#c05621]" },
  { key: "blue",   bg: "bg-[#ebf8ff]",   accent: "border-l-[#90cdf4]",   pill: "bg-[#ebf8ff]",   pillText: "text-[#2b6cb0]" },
  { key: "red",    bg: "bg-[#fff5f5]",   accent: "border-l-[#feb2b2]",   pill: "bg-[#fff5f5]",   pillText: "text-[#c53030]" },
  { key: "violet", bg: "bg-[#faf5ff]",   accent: "border-l-[#c4b5fd]",   pill: "bg-[#faf5ff]",   pillText: "text-[#6b46c1]" },
  { key: "teal",   bg: "bg-[#4fd1c5]/10", accent: "border-l-[#4fd1c5]",  pill: "bg-[#4fd1c5]/15", pillText: "text-[#2c7a7b]" },
];

const SOURCE_COLOURS = {
  "Whitespark": PALETTE[0],
  "Search Engine Journal": PALETTE[1],
  "Moz Blog": PALETTE[2],
  "Google Search Central": PALETTE[3],
  "Backlinko": PALETTE[4],
  "Ahrefs Blog": PALETTE[5],
};

function hashIndex(s, mod) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h % mod;
}

function colourFor(source) {
  return SOURCE_COLOURS[source] || PALETTE[hashIndex(source || "x", PALETTE.length)];
}

function ArticleCard({ it, i }) {
  const c = colourFor(it.source);
  return (
    <a
      href={it.link}
      target="_blank"
      rel="noreferrer noopener"
      data-testid={`news-article-${i}`}
      className={`block rounded-xl ${c.bg} border border-[#edf2f7] border-l-4 ${c.accent} p-4 hover:shadow-md hover:-translate-y-0.5 transition-all group h-full`}
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <span
          className={`inline-block ${c.pill} ${c.pillText} text-[9px] uppercase tracking-widest font-bold rounded-full px-2 py-0.5 border border-current/20`}
          style={{ borderColor: "currentColor" }}
        >
          {it.source}
        </span>
        <ArrowUpRight className="h-3 w-3 text-[#a0aec0] opacity-0 group-hover:opacity-100 transition-opacity flex-none" />
      </div>
      <div className="text-sm text-[#1a202c] group-hover:text-[#2c7a7b] font-semibold leading-snug line-clamp-2 min-h-[2.6em]">
        {it.title}
      </div>
      {it.summary && (
        <div className="text-[11px] text-[#718096] leading-relaxed mt-2 line-clamp-3">
          {it.summary}
        </div>
      )}
      <div className="text-[10px] text-[#a0aec0] font-mono-tight mt-3">
        {it.published_at ? timeAgo(it.published_at) : "—"}
      </div>
    </a>
  );
}

export default function IndustryNewsCard({ data, loading, onRefresh, refreshing }) {
  const items = data?.items || [];
  const sourceCount = new Set(items.map((it) => it.source)).size;

  return (
    <div className="card-soft overflow-hidden" data-testid="industry-news-card">
      <div className="px-5 py-4 border-b border-[#edf2f7] flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2.5">
          <div className="h-7 w-7 rounded-md bg-[#faf5ff] border border-[#e9d8fd] flex items-center justify-center">
            <Newspaper className="h-3.5 w-3.5 text-[#6b46c1]" strokeWidth={2.4} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#a0aec0] font-bold">What's happening in SEO</div>
            <div className="text-sm font-display text-[#1a202c] mt-0.5">
              Industry news
              {sourceCount ? (
                <span className="text-[11px] text-[#a0aec0] font-mono-tight ml-2 font-normal">
                  · {sourceCount} source{sourceCount === 1 ? "" : "s"} · {items.length} article{items.length === 1 ? "" : "s"}
                </span>
              ) : null}
            </div>
          </div>
        </div>
        <button
          onClick={onRefresh}
          disabled={loading || refreshing}
          data-testid="news-refresh"
          title="Refresh (hourly cache)"
          className="btn-ghost inline-flex items-center gap-1.5 text-[11px] py-1.5"
        >
          {refreshing ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
          Refresh
        </button>
      </div>

      {loading && !items.length ? (
        <div className="px-5 py-14 text-center text-[11px] text-[#a0aec0]">
          <Loader2 className="h-5 w-5 animate-spin mx-auto mb-3" /> Fetching the latest from across the web…
        </div>
      ) : items.length === 0 ? (
        <div className="px-5 py-10 text-[11px] text-[#a0aec0] italic text-center">
          No news items right now — the configured feeds may be down. Check Settings.
        </div>
      ) : (
        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {items.map((it, i) => (
            <ArticleCard key={`${it.link}-${i}`} it={it} i={i} />
          ))}
        </div>
      )}

      {data?.fetched_at && (
        <div className="px-5 py-2 border-t border-[#edf2f7] text-[9px] text-[#a0aec0] font-mono-tight bg-[#fafbfc] flex items-center justify-between">
          <span>Updated {timeAgo(data.fetched_at)}{data.cached && " · from cache"}</span>
          <span>Articles update hourly · edit sources in App Settings</span>
        </div>
      )}
    </div>
  );
}
